import React, {
  FC,
  memo,
  useEffect,
  useMemo,
  useState,
  useCallback,
  // useRef,
} from 'react';
import { useTranslate } from '@/translations';
import {
  CustomColumns,
  CustomTable,
  faAngleRight,
  faCopy,
  faStepBackward,
  faStepForward,
  faXmark,
  Icon,
  VehicleInformation,
} from '@/ui/components';
import {
  App,
  Button,
  Card,
  Col,
  DatePicker,
  Drawer,
  Row,
  Space,
  Tabs,
  Tooltip,
  Typography,
} from 'antd';
import { baseSort, timeConverter } from '@/utils/helpers';
import { FORMAT_DATE_DAYJS, FORMAT_FULL_TIME_DAYJS } from '@/utils/constants';

import dayjs, { Dayjs } from 'dayjs';
import { transportMovements } from '@/api/TransportMovements';
import { LoadDetails, SelectOption, TransportMovement } from '@/types';
import { LoadNoContainer } from './TransportMovementDataGrid.styled';
import { TransportMovementLogs } from '../TransportMovementLogs/TransportMovementLogs';

export const TransportMovementDataGrid: FC<{
  scrollContainerRef?: React.RefObject<HTMLDivElement | null>;
}> = ({ scrollContainerRef }) => {
  const { t } = useTranslate();
  const { message } = App.useApp();
  const [loading, setLoading] = useState(false);
  const [selectedLoad, setSelectedLoad] = useState<TransportMovement | null>(
    null,
  );
  const [selectedLoadVehicles, setSelectedLoadVehicles] = useState<
    LoadDetails[]
  >([]);
  const [isLoadDrawerOpen, setIsLoadDrawerOpen] = useState(false);
  const [selectedLoadKey, setSelectedLoadKey] = useState<number | null>(0);

  const [selectedDate, setSelectedDate] = useState<Dayjs>(dayjs());
  const [movementsData, setMovements] = useState<TransportMovement[]>([]);

  const [loadStatusFilters, setLoadStatusFilters] = useState<
    SelectOption<string | number>[]
  >([]);
  const [vehicleCountFilters, setVehicleCountFilters] = useState<
    SelectOption<string | number>[]
  >([]);

  const [siteFilters, setSiteFilters] = useState<
    SelectOption<string | number>[]
  >([]);
  const [loadDetailsToggle, setLoadDetailsToggle] =
    useState<string>('vehicleDetails');
  const [windowWidth, setwindowWidth] = useState(window.innerWidth);

  useEffect(() => {
    const handleResize = () => setwindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (selectedLoad && isLoadDrawerOpen && scrollContainerRef?.current) {
      scrollContainerRef.current.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    }
  }, [selectedLoad, isLoadDrawerOpen, scrollContainerRef]);

  const currentLoadIndex = movementsData.findIndex(
    (load) => load.loadId === selectedLoadKey,
  );

  const getFilterOptions = <T extends keyof TransportMovement>(
    data: TransportMovement[],
    key: T,
  ) => {
    const uniqueItems = [
      ...new Set(data.map((item) => item[key]).filter(Boolean)),
    ];
    const wrappedItems = uniqueItems.map((value) => ({ value }));
    const sortItems = wrappedItems.sort((a, b) => baseSort(a, b, 'value'));
    const sortedItems = sortItems.map(({ value }) => ({
      text: String(value),
      value,
    }));

    return sortedItems;
  };

  useEffect(() => {
    const fetchMovements = async () => {
      setLoading(true);
      const formatedDate = dayjs(selectedDate).format('YYYY-MM-DD');
      const response = await transportMovements.getTransportMovements({
        RequestedDate: formatedDate,
      });
      const data = response.data?.entities ?? [];
      setLoadStatusFilters(getFilterOptions(data, 'statusDesc'));
      setVehicleCountFilters(getFilterOptions(data, 'vehicleCount'));
      setSiteFilters(getFilterOptions(data, 'fromLocation'));

      setMovements(data);
      setLoading(false);
    };
    fetchMovements();
  }, [selectedDate]);

  useEffect(() => {
    const fetchLoadDetails = async () => {
      if (!selectedLoadKey) return;
      setLoading(true);
      const response = await transportMovements.getloadDetails(selectedLoadKey);
      setSelectedLoadVehicles(response.data?.entities ?? []);
      setLoading(false);
    };
    fetchLoadDetails();
  }, [selectedLoadKey]);

  const handleLoadRowClick = (record: TransportMovement) => {
    setSelectedLoadKey(record.loadId);
    setSelectedLoad(record);
    if (record.vehicleCount > 0) {
      setIsLoadDrawerOpen(true);
    } else {
      setIsLoadDrawerOpen(false);
    }
  };

  const handleLoadDrawerClose = () => {
    setIsLoadDrawerOpen(false);
    setSelectedLoad(null);
    setSelectedLoadKey(null);
    setSelectedLoad(null);
  };

  const goToPreviousLoad = useCallback(() => {
    const prevIndex = currentLoadIndex - 1;
    if (prevIndex >= 0) {
      handleLoadRowClick(movementsData[prevIndex]);
    }
  }, [currentLoadIndex, movementsData]);

  const goToNextLoad = useCallback(() => {
    const nextIndex = currentLoadIndex + 1;
    if (nextIndex < movementsData.length) {
      handleLoadRowClick(movementsData[nextIndex]);
    }
  }, [currentLoadIndex, movementsData]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') {
        if (isLoadDrawerOpen) {
          goToNextLoad();
        }
      } else if (e.key === 'ArrowLeft') {
        if (isLoadDrawerOpen) {
          goToPreviousLoad();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isLoadDrawerOpen, goToNextLoad, goToPreviousLoad]);

  const loadColumns: CustomColumns<TransportMovement> = useMemo(() => {
    const baseColumns: CustomColumns<TransportMovement> = [
      {
        dataIndex: 'loadNumber',
        title: t('loadNumber'),
        sortable: true,
        filter: true,
        ellipsis: true,
        onFilter: (value, record) =>
          record.loadNumber
            ?.toString()
            .toLowerCase()
            .includes(value?.toString()?.toLowerCase()),
        render: (value, record) => (
          <LoadNoContainer $status={record.loadId === selectedLoadKey}>
            {value} <Icon icon={faAngleRight} />
          </LoadNoContainer>
        ),
      },
      {
        dataIndex: 'statusDesc',
        title: t('loadStatus'),
        renderType: 'select',
        ellipsis: true,
        sortable: true,
        filter: true,
        filterOptions: loadStatusFilters,
      },
      {
        dataIndex: 'vehicleCount',
        title: t('numberOfVehicles'),
        renderType: 'select',
        sortable: true,
        ellipsis: true,
        filter: true,
        filterOptions: vehicleCountFilters,
      },
      {
        dataIndex: 'etaDate',
        title: t('requestDateTime'),
        sortable: true,
        ellipsis: true,
        renderType: 'date',
        render: (value) => timeConverter(value, FORMAT_FULL_TIME_DAYJS),
      },
      {
        dataIndex: 'collectionDate',
        title: t('collectionDateTime'),
        sortable: true,
        ellipsis: true,
        renderType: 'date',
        render: (value) => timeConverter(value, FORMAT_FULL_TIME_DAYJS),
      },
      {
        dataIndex: 'fromLocation',
        title: t('currentSite'),
        renderType: 'select',
        sortable: true,
        ellipsis: true,
        filter: true,
        filterOptions: siteFilters,
      },
      {
        dataIndex: 'toLocation',
        title: t('toLocation'),
        sortable: true,
        filter: true,
        ellipsis: true,
      },
      {
        dataIndex: 'providerName',
        title: t('transportCompany'),
        sortable: true,
        filter: true,
        ellipsis: true,
      },
      {
        dataIndex: 'unitName',
        title: t('unitName'),
        sortable: true,
        filter: true,
        ellipsis: true,
      },
    ];
    if (windowWidth < 1400) {
      return baseColumns.filter(
        (col) => col.dataIndex !== 'unitName' && col.dataIndex !== 'toLocation',
      );
    }
    return baseColumns;
  }, [
    t,
    selectedLoadKey,
    loadStatusFilters,
    vehicleCountFilters,
    siteFilters,
    windowWidth,
  ]);

  const vehicleColumns: CustomColumns<LoadDetails> = useMemo(
    () => [
      {
        dataIndex: 'vin',
        title: t('vin'),
        width: 220,
        filter: true,
        onFilter: (value, record) =>
          record.vin
            ?.toString()
            .toLowerCase()
            .includes(value?.toString()?.toLowerCase()),
        render: (value) => (
          <div className="d-flex align-items-center justify-content-between gap-2">
            <VehicleInformation vin={value} showAsLabel />
            <Icon
              icon={faCopy}
              color="#F58720"
              style={{ cursor: 'pointer' }}
              onClick={() => {
                navigator.clipboard.writeText(value || '');
                message.success('Copied to clipboard');
              }}
            />
          </div>
        ),
      },
      {
        dataIndex: 'make',
        title: t('make'),
        align: 'center',
        width: 110,
        sortable: true,
        filter: true,
        ellipsis: true,
      },
      {
        dataIndex: 'model',
        title: t('model'),
        sortable: true,
        filter: true,
        ellipsis: true,
      },
      {
        dataIndex: 'colour',
        title: t('colour'),
        width: 140,
        sortable: true,
        filter: true,
        ellipsis: true,
      },
      {
        dataIndex: 'vinStatus',
        title: t('status'),
        align: 'center',
        width: 90,
        sortable: true,
        filter: true,
      },
      {
        dataIndex: 'location',
        title: t('location'),
        sortable: true,
        filter: true,
        ellipsis: true,
      },
    ],
    [message, t],
  );

  const loadDetailsTitle = () => (
    <Row justify="space-between" align="middle">
      <Col>
        <Space>
          <Typography.Title level={5} style={{ margin: 0 }}>
            Load No: {selectedLoad?.loadNumber ?? ''}
          </Typography.Title>
          <Space>
            <Tooltip title="Previous Load">
              <span>
                <Button
                  icon={<Icon icon={faStepBackward} />}
                  onClick={goToPreviousLoad}
                />
              </span>
            </Tooltip>
            <Tooltip title="Next Load">
              <span>
                <Button
                  icon={<Icon icon={faStepForward} />}
                  onClick={goToNextLoad}
                />
              </span>
            </Tooltip>
          </Space>
        </Space>
      </Col>

      <Col>
        <Tooltip title="Close">
          <span>
            <Icon
              icon={faXmark}
              onClick={handleLoadDrawerClose}
              placement="right"
            />
          </span>
        </Tooltip>
      </Col>
    </Row>
  );

  return (
    <Card>
      <Space style={{ marginBottom: 25, marginLeft: 16 }}>
        <Typography.Text strong style={{ margin: 15 }}>
          {t('loadRequest')}:
        </Typography.Text>
      </Space>

      <DatePicker
        placeholder={t('chooseDate')}
        format={{ format: FORMAT_DATE_DAYJS }}
        onChange={(date) => {
          setIsLoadDrawerOpen(false);
          setSelectedDate(date);
        }}
        value={selectedDate}
      />
      <CustomTable
        dataSource={movementsData}
        loading={loading}
        columns={loadColumns}
        rowKey="loadNumber"
        pagination={{ showSizeChanger: true }}
        // style={{ minHeight: '60vh' }}
        onRow={(record) => ({ onClick: () => handleLoadRowClick(record) })}
        rowClassName={(record) =>
          record.loadId === selectedLoadKey ? 'selected-row' : ''
        }
      />

      <Drawer
        title={loadDetailsTitle()}
        placement="right"
        onClose={handleLoadDrawerClose}
        open={isLoadDrawerOpen}
        width={1100}
        mask={false}
        closable={false}
      >
        <Tabs
          activeKey={loadDetailsToggle}
          onChange={setLoadDetailsToggle}
          type="card"
          className="vehicle-information-tabs"
          items={[
            {
              key: 'vehicleDetails',
              label: t('vehicle'),
              children: (
                <div key="vehicleDetails">
                  <CustomTable
                    dataSource={selectedLoadVehicles || []}
                    columns={vehicleColumns}
                    loading={loading}
                    rowKey="vin"
                    pagination={{ showSizeChanger: true }}
                  />
                </div>
              ),
            },
            {
              key: 'logs',
              label: t('logs'),
              children: (
                <div key={`logs-${selectedLoadKey}`}>
                  <TransportMovementLogs
                    loadId={selectedLoadKey || 0}
                    loadNumber={selectedLoad?.loadNumber ?? ''}
                  />
                </div>
              ),
            },
          ]}
        />

        {/* <Tabs
          defaultActiveKey="1"
          type="card"
          style={{ marginBottom: 32 }}
          items={Array.from({ length: 3 }).map((_, i) => {
            const id = String(i + 1);
            return {
              label: `Card Tab ${id}`,
              key: id,
              children: `Content of card tab ${id}`,
            };
          })}
        /> */}
        {/* {loadDetailsToggle === 'vehicleDetails' && (
          <CustomTable
            dataSource={selectedLoadVehicles || []}
            columns={vehicleColumns}
            loading={loading}
            rowKey="vin"
            pagination={{ showSizeChanger: true }}
          />
        )} */}

        {/* {loadDetailsToggle === 'logs' && (
          <TransportMovementLogs
            loadId={selectedLoadKey || 0}
            loadNumber={selectedLoad?.loadNumber ?? ''}
          />
        )} */}
      </Drawer>
    </Card>
  );
};

export const TransportDataGrid = memo(TransportMovementDataGrid);
